import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

// Hand-written until the OpenAPI types are regenerated.

/** One numbered point of a contract article. */
export type ClausePoint = {
  /** Article key + standard number, e.g. "IV.3". */
  id: string
  /** Standard number; the printed one is lower when earlier points are dropped. */
  number: number
  /** Standard wording, with the offer's / flat's data filled in. */
  default_text: string
  /** Admin's wording, null when the point is standard. */
  text: string | null
  /** The standard wording changed since the point was edited (e.g. rent). */
  stale: boolean
  /** Left out of the contract; the following points move up. */
  dropped: boolean
  /** Cannot be dropped, only rewritten. */
  fixed: boolean
  /** Ids of the points the standard wording refers to by number. */
  refs: string[]
}

export type ClauseArticle = {
  key: string
  num: string
  title: string
  points: ClausePoint[]
  /** Points appended by an admin after the article's last point. */
  added: string[]
}

export type ClauseEdits = {
  edited: Record<string, string>
  added: Record<string, string[]>
  dropped: string[]
}

/** Which contract document the editor works on, with the terms it renders. */
export type ClauseTarget =
  | {
      kind: 'offer'
      offerId: string
      startDate: string
      endDate: string
      snaFee: boolean
    }
  | {
      kind: 'flat'
      flatId: string
      paymentTime: string
      /** Undefined keeps what the flat has stored. */
      depositPaid?: boolean
    }

function targetParams(target: ClauseTarget) {
  return target.kind === 'offer'
    ? {
        offer_id: target.offerId,
        start_date: target.startDate,
        end_date: target.endDate,
        sna_fee: target.snaFee
      }
    : {
        flat_id: target.flatId,
        payment_time: target.paymentTime,
        deposit_paid: target.depositPaid
      }
}

const clausesUrl = (target: ClauseTarget) =>
  `${SNA_PREFIX}contracts/${target.kind === 'offer' ? 'offers' : 'flats'}/clauses`

export const contractClausesQueryKey = ['contract-clauses']

export function useContractClauses(target: ClauseTarget, enabled = true) {
  return useQuery({
    queryKey: [...contractClausesQueryKey, targetParams(target)],
    queryFn: async () => {
      const res = await apiClient.get<ClauseArticle[]>(clausesUrl(target), {
        params: targetParams(target)
      })
      return res.data
    },
    enabled,
    // The editor holds its own draft; refetching under it would be confusing.
    refetchOnWindowFocus: false
  })
}

export function useSaveContractClauses(target: ClauseTarget) {
  const queryClient = useQueryClient()
  const onError = useOnError()

  return useMutation({
    mutationFn: async (edits: ClauseEdits) => {
      const res = await apiClient.put<ClauseArticle[]>(clausesUrl(target), {
        ...targetParams(target),
        ...edits
      })
      return res.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...contractClausesQueryKey, targetParams(target)],
        data
      )
    },
    onError
  })
}

/** The contract PDF with edited points highlighted, for the inline preview. */
export function useClausesPreview(target: ClauseTarget) {
  const onError = useOnError()

  return useMutation({
    mutationFn: async () => {
      const url =
        target.kind === 'offer'
          ? `${SNA_PREFIX}contracts/offers/preview`
          : `${SNA_PREFIX}contracts/flats/preview`
      const res = await apiClient.get<Blob>(url, {
        params: { ...targetParams(target), highlight: true },
        responseType: 'blob'
      })
      // Errors come back as a JSON blob because of responseType.
      if (res.data.type === 'application/json') {
        throw JSON.parse(await res.data.text())
      }
      return new Blob([res.data], { type: 'application/pdf' })
    },
    onError
  })
}

export function useResendContract() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('clauses')
  const onError = useOnError()

  return useMutation({
    mutationFn: async ({
      id,
      type,
      snaFee
    }: {
      id: string
      type: 'offer' | 'flat'
      /** Only for tenant contracts that do not know their SNA fee yet. */
      snaFee?: boolean
    }) => {
      const res = await apiClient.post(
        `${SNA_PREFIX}contracts/${id}/resend`,
        { sna_fee: snaFee ?? null },
        { params: { type } }
      )
      return res.data
    },
    onSuccess: () => {
      toast.success({ message: t('resent') })
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
    },
    onError
  })
}
